import {
  ProductReviewFilterValues,
  ProductReviewsDataMapper,
  ReviewsData,
  SortOptions,
} from '@lib/types/api/Review';
import { ReviewFormData } from '@ui-components/index';
import { useCallback, useState } from 'react';
import { createMarketplaceClient } from '../../../../../lib/api/usg/marketplace';
import { PAGE_SIZE } from '../constants/reviews';
import { productReviewsDataMapper } from '../mappers/productReviewsDataMapper';

export type UseReviewsParams = {
  productId: string;
  publisherSupportContact?: string;
  isMultiPackage?: boolean;
};

export const mapReviewData = (
  review: ProductReviewsDataMapper,
  contextData: {
    ownedDate: string;
    writeReviewModalTitle: string;
    writeReviewModalImageUrl: string;
    writeReviewModalVersion: string;
    writeReviewModalOwnedDate: string;
    isMultiPackage: boolean;
  }
) => ({
  reviewerInfo: review.reviewerInfo,
  reviewDetails: {
    id: review.reviewDetails.id,
    ownedDate: contextData.ownedDate,
    canSubmitReview: true,
    stars: review.reviewDetails.stars,
    gifted: review.reviewDetails.gifted,
    version: review.reviewDetails.version,
    title: review.reviewDetails.title,
    review: review.reviewDetails.review,
    publishedDate: review.reviewDetails.publishedDate,
    editedDate: undefined,
    canReportReview: review.reviewDetails.canReportReview,
    canHelpfulVotes: review.reviewDetails.canHelpfulVotes,
    canEdit: review.reviewDetails.canEdit,
    canDelete: review.reviewDetails.canDelete,
    canReply: review.reviewDetails.canReply,
    reply: undefined,
    writeReviewModal: {
      title: contextData.writeReviewModalTitle,
      imageUrl: contextData.writeReviewModalImageUrl,
      version: contextData.writeReviewModalVersion,
      ownedDate: contextData.writeReviewModalOwnedDate,
      isMultiPackage: contextData.isMultiPackage,
      data: review.reviewDetails.writeReviewModal?.data
        ? {
            id: review.reviewDetails.id,
            rating: review.reviewDetails.writeReviewModal.data.rating,
            title: review.reviewDetails.writeReviewModal.data.title,
            review: review.reviewDetails.writeReviewModal.data.review,
          }
        : undefined,
    },
  },
});

const marketplaceClient = createMarketplaceClient();

export const useReviews = ({
  productId,
  publisherSupportContact,
  isMultiPackage = false,
}: UseReviewsParams) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewsData, setReviewsData] = useState<ReviewsData | undefined>(
    undefined
  );
  const [activeFilter, setActiveFilter] = useState<ProductReviewFilterValues>();
  const [activeSort, setActiveSort] = useState<string>('recent');

  const getReviews = useCallback(
    async (
      page: number,
      filter?: ProductReviewFilterValues,
      sort?: string
    ): Promise<ReviewsData | undefined> => {
      if (!productId) {
        setError('Product ID is required');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const start = (page - 1) * PAGE_SIZE;

        const response = await marketplaceClient.listReviews({
          productId,
          start,
          count: PAGE_SIZE,
          filterRating: filter,
          sort: sort as unknown as SortOptions,
        });

        const mappedReviews = response.items.map((item) => {
          const mappedReview = productReviewsDataMapper(
            { ...item },
            { includeWriteReviewModal: true, isMultiPackage }
          );

          return {
            ...mappedReview,
            reviewDetails: {
              ...mappedReview.reviewDetails,
              ownedDate: mappedReview.reviewDetails.ownedDate || '',
              canSubmitReview: mappedReview.reviewDetails.canSubmitReview,
              isMultiPackage,
            },
          };
        });

        const reviewsData: ReviewsData = {
          reviews: mappedReviews,
          total: response.total,
          start: response.start + 1,
          count: response.count,
          currentPage:
            response.count > 0
              ? Math.floor(response.start / response.count) + 1
              : 1,
          totalPages: Math.ceil(response.total / PAGE_SIZE),
          end: response.start + mappedReviews.length,
          activeFilterValue: filter,
        };

        setReviewsData(reviewsData);

        return reviewsData;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Reviews fetch failed:', err);
      } finally {
        setLoading(false);
      }
    },
    [productId, isMultiPackage]
  );

  // Review Actions
  const onFilter = useCallback(
    async (filter: string) => {
      const stars = Number(filter);
      const filterValue = (stars === 0
        ? ''
        : filter) as unknown as ProductReviewFilterValues;

      setActiveFilter(filterValue === '' ? undefined : filterValue);

      const reviewsData = await getReviews(
        1,
        filterValue === '' ? undefined : filterValue,
        activeSort
      );

      return reviewsData;
    },
    [getReviews, activeSort]
  );

  const onSort = useCallback(
    async (sort: string) => {
      setActiveSort(sort);
      const reviewsData = await getReviews(1, activeFilter, sort);
      return reviewsData;
    },
    [getReviews, activeFilter]
  );

  const onPageChange = useCallback(
    async (page: number) => {
      return await getReviews(page, activeFilter, activeSort);
    },
    [getReviews, activeFilter, activeSort]
  );

  const onSubmitReport = useCallback(
    async (
      reviewId: string,
      productId: string,
      report: { reportReview: string; type: string }
    ) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}/report`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: report.reportReview,
            type: report.type,
          }),
        });

        if (!res.ok) throw new Error('Error reporting review');
      } catch (error) {
        console.error('Report failed:', error);
        throw error;
      }
    },
    []
  );

  const onSubmitReview = useCallback(
    async (productId: string, formData: ReviewFormData) => {
      console.log('productId', productId);
      console.log('formData', formData);

      await new Promise((resolve) => setTimeout(resolve, 3000));
      throw new Error('Internal Server Error');

      /*
        try {
          const url = new URL(
            `/api/products/${productId}/reviews`,
            window.location.origin
          );

          const res = await fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
          });

          if (!res.ok) throw new Error('Error creating review');

          setActiveFilter(undefined);
          const reviewsData = await getReviews(1, undefined, 'recent');
          return reviewsData;
        } catch (error) {
          console.error('Submit review failed:', error);
          throw error;
        }
      */
    },
    // [getReviews]
    []
  );

  const onDeleteReview = useCallback(
    async (reviewId: string, mock?: boolean) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), { method: 'DELETE' });
        if (!res.ok) throw new Error('Error deleting review');

        const reviewsData = await getReviews(1, activeFilter, activeSort);
        // Optional client-side adjustment used when callers operate in a mocked environment.
        // TODO: Remove or replace this block when delete-review flows in all environments
        // provide an updated reviews list without requiring this local patch.
        if (mock && reviewsData && Array.isArray(reviewsData.reviews)) {
          const filteredReviews = reviewsData.reviews.filter(
            (review) => review.reviewDetails.id !== reviewId
          );
          const updatedReviewsData = {
            ...reviewsData,
            reviews: filteredReviews,
            total: reviewsData.total - 1,
            end: reviewsData.start + filteredReviews.length,
          };
          setReviewsData(updatedReviewsData);
          return updatedReviewsData;
        }
        return reviewsData;
      } catch (error) {
        console.error('Delete review failed:', error);
        throw error;
      }
    },
    [productId, getReviews, activeFilter, activeSort]
  );

  const onEditReview = useCallback(
    async (reviewId: string, formData: ReviewFormData) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) throw new Error('Error editing review');

        const reviewsData = await getReviews(1, activeFilter, activeSort);
        return reviewsData;
      } catch (error) {
        console.error('Edit review failed:', error);
        throw error;
      }
    },
    [productId, getReviews, activeFilter, activeSort]
  );

  const onEmailPublisher = useCallback(() => {
    if (
      !publisherSupportContact ||
      typeof publisherSupportContact !== 'string' ||
      publisherSupportContact.trim().length === 0
    ) {
      console.warn('Publisher support contact is not available or invalid.');
      return;
    }
    window.location.href = `mailto:${publisherSupportContact}`;
  }, [publisherSupportContact]);

  // TODO: These features are not going to be part of the GA launch
  const onHelpfulVotesReview = useCallback((reviewId: string) => {
    console.log('Helpful votes clicked for review:', reviewId);
  }, []);

  const onSubmitReply = useCallback(
    (reply: string, replyId?: string | undefined) => {
      console.log('Submit reply clicked:', reply, replyId);
    },
    []
  );

  const onHelpfulVotesReply = useCallback((replyId: string) => {
    console.log('Helpful votes clicked for reply:', replyId);
  }, []);

  const onEditReply = useCallback((replyId: string) => {
    console.log('Edit reply clicked for reply:', replyId);
  }, []);

  const onDeleteReply = useCallback((replyId: string) => {
    console.log('Delete reply clicked for reply:', replyId);
  }, []);

  return {
    // Data
    reviewsData,
    getReviews,
    loading,
    error,
    activeFilter,
    activeSort,

    // Actions
    onFilter,
    onSort,
    onPageChange,
    onSubmitReport,
    onSubmitReview,
    onDeleteReview,
    onEditReview,
    onEmailPublisher,
    onHelpfulVotesReview,
    onSubmitReply,
    onHelpfulVotesReply,
    onEditReply,
    onDeleteReply,
  };
};
